import type { EraDef } from '../content/types';
import { ProceduralMusic, type MusicState } from './music';

/**
 * AUDIO HOOKS
 * Everything that makes sound goes through this interface so a later pass can
 * drop in real assets. The default implementation synthesizes short blips with
 * the Web Audio API (no asset files), rate-limited so bullet-hell density
 * doesn't produce a wall of noise, plus a fully procedural per-era music
 * track (see `music.ts`) — also no asset files. See docs/DECISIONS.md for
 * why (offline sandbox, can't download audio assets) and the swap-in path
 * for real files later.
 */
export interface AudioHooks {
  play(id: string, x?: number, y?: number): void;
  setEra(era: EraDef): void;
  setMusic(track: MusicState): void;
  /** Overall/master volume (multiplies both music and SFX). */
  setVolume(v: number): void;
  setMusicVolume(v: number): void;
  setSfxVolume(v: number): void;
  resume(): void;
}

interface Blip {
  freq: number;
  type: OscillatorType;
  dur: number;
  gain: number;
  slide?: number;
  minGap: number;
}

// Sounds handled by the generic single-oscillator path below. A few ids
// ('shoot', 'kill', 'bossSpawn', 'levelUp', 'timeJump') are special-cased in
// `play()` for a richer sound and are NOT listed here — their minGaps live
// in SPECIAL_MIN_GAP instead.
const BLIPS: Record<string, Blip> = {
  hit: { freq: 220, type: 'square', dur: 0.03, gain: 0.05, minGap: 0.05 },
  crit: { freq: 880, type: 'square', dur: 0.06, gain: 0.09, slide: 300, minGap: 0.05 },
  killElite: { freq: 120, type: 'sawtooth', dur: 0.35, gain: 0.2, slide: -80, minGap: 0.1 },
  playerHit: { freq: 90, type: 'square', dur: 0.2, gain: 0.25, slide: -40, minGap: 0.1 },
  playerDeath: { freq: 200, type: 'sawtooth', dur: 1.2, gain: 0.3, slide: -180, minGap: 0.5 },
  dash: { freq: 260, type: 'sawtooth', dur: 0.18, gain: 0.14, slide: 780, minGap: 0.1 },
  xp: { freq: 1200, type: 'sine', dur: 0.03, gain: 0.03, minGap: 0.03 },
  heal: { freq: 700, type: 'sine', dur: 0.25, gain: 0.15, slide: 300, minGap: 0.1 },
  shard: { freq: 1500, type: 'triangle', dur: 0.15, gain: 0.12, slide: 400, minGap: 0.05 },
  bossPhase: { freq: 100, type: 'square', dur: 0.6, gain: 0.25, slide: 120, minGap: 0.5 },
  bossDeath: { freq: 300, type: 'sawtooth', dur: 2, gain: 0.35, slide: -250, minGap: 1 },
  rewind: { freq: 300, type: 'sine', dur: 0.6, gain: 0.2, slide: 900, minGap: 0.3 },
  chain: { freq: 1400, type: 'square', dur: 0.04, gain: 0.06, minGap: 0.04 },
  ui: { freq: 600, type: 'sine', dur: 0.05, gain: 0.08, minGap: 0.03 },
  // 0.6.0 signature-behavior / boss-special cues (sim pushes these as { type: 'sfx', id })
  burrow: { freq: 180, type: 'triangle', dur: 0.3, gain: 0.12, slide: -120, minGap: 0.15 },
  emerge: { freq: 140, type: 'sawtooth', dur: 0.25, gain: 0.16, slide: 260, minGap: 0.15 },
  blink: { freq: 900, type: 'sine', dur: 0.12, gain: 0.1, slide: -500, minGap: 0.1 },
  stomp: { freq: 70, type: 'square', dur: 0.3, gain: 0.22, slide: -40, minGap: 0.12 },
  roar: { freq: 110, type: 'sawtooth', dur: 0.8, gain: 0.28, slide: 60, minGap: 0.6 },
  pull: { freq: 250, type: 'sine', dur: 1.2, gain: 0.18, slide: -180, minGap: 1 },
  shieldBreak: { freq: 1000, type: 'square', dur: 0.25, gain: 0.16, slide: -700, minGap: 0.2 },
  // 0.7.0 (eras 4-6 signature behaviors)
  slide: { freq: 320, type: 'sine', dur: 0.4, gain: 0.12, slide: -160, minGap: 0.2 },
  block: { freq: 500, type: 'square', dur: 0.05, gain: 0.07, slide: -200, minGap: 0.08 },
  quake: { freq: 60, type: 'sawtooth', dur: 0.9, gain: 0.26, slide: -25, minGap: 0.8 },
  // 0.14.0 (era 8 signature behaviors / boss special)
  klaxon: { freq: 480, type: 'sawtooth', dur: 0.45, gain: 0.22, slide: -220, minGap: 0.5 },
  stamp: { freq: 90, type: 'square', dur: 0.35, gain: 0.24, slide: -60, minGap: 0.15 },
  steamvent: { freq: 650, type: 'sawtooth', dur: 0.4, gain: 0.14, slide: -380, minGap: 0.3 },
  bombdrop: { freq: 500, type: 'sine', dur: 0.5, gain: 0.14, slide: -400, minGap: 0.2 },
  // 0.16.0 (era 9 signature behaviors / boss special)
  lockping: { freq: 1600, type: 'sine', dur: 0.08, gain: 0.09, slide: 200, minGap: 0.1 },
  lockon: { freq: 900, type: 'square', dur: 0.2, gain: 0.14, slide: -60, minGap: 0.15 },
  lance: { freq: 1100, type: 'sawtooth', dur: 0.15, gain: 0.2, slide: -900, minGap: 0.1 },
  podeploy: { freq: 130, type: 'square', dur: 0.3, gain: 0.2, slide: 40, minGap: 0.2 },
  systemsfail: { freq: 500, type: 'sawtooth', dur: 0.7, gain: 0.24, slide: -420, minGap: 0.4 },
  // 0.17.0 (era 10 signature behaviors / boss special)
  echoping: { freq: 1250, type: 'sine', dur: 0.06, gain: 0.07, slide: -150, minGap: 0.08 },
  unwind: { freq: 420, type: 'triangle', dur: 0.35, gain: 0.15, slide: -60, minGap: 0.2 },
  snapback: { freq: 260, type: 'sine', dur: 0.45, gain: 0.18, slide: 520, minGap: 0.2 },
  devour: { freq: 70, type: 'sawtooth', dur: 1, gain: 0.16, slide: -15, minGap: 1 },
  unmade: { freq: 620, type: 'sawtooth', dur: 0.8, gain: 0.26, slide: -480, minGap: 0.4 },
};

const SPECIAL_MIN_GAP: Record<string, number> = {
  shoot: 0.07,
  kill: 0.04,
  bossSpawn: 1,
  levelUp: 0.2,
  timeJump: 1,
};

export class SynthAudio implements AudioHooks {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private music: ProceduralMusic | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private last = new Map<string, number>();
  private masterVolume = 1;
  private sfxVolume = 0.7;
  private musicVolume = 0.5;
  private eraPitch = 1;
  private pendingEraIndex = 0;
  private pendingTrack: MusicState = 'none';

  private ensure(): boolean {
    if (this.ctx) return true;
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.masterVolume;
      this.master.connect(this.ctx.destination);

      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.sfxVolume;
      this.sfxGain.connect(this.master);

      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = this.musicVolume;
      this.musicGain.connect(this.master);

      this.music = new ProceduralMusic(this.ctx, this.musicGain);
      this.music.setEra(this.pendingEraIndex);
      this.music.setState(this.pendingTrack);
      return true;
    } catch {
      return false;
    }
  }

  resume(): void {
    if (this.ensure() && this.ctx!.state === 'suspended') void this.ctx!.resume();
  }

  setVolume(v: number): void {
    this.masterVolume = v;
    if (this.master) this.master.gain.value = v;
  }

  setMusicVolume(v: number): void {
    this.musicVolume = v;
    if (this.musicGain) this.musicGain.gain.value = v;
  }

  setSfxVolume(v: number): void {
    this.sfxVolume = v;
    if (this.sfxGain) this.sfxGain.gain.value = v;
  }

  setEra(era: EraDef): void {
    // era-specific "instrument": later eras are pitched up slightly
    this.eraPitch = 1 + era.index * 0.03;
    this.pendingEraIndex = era.index;
    if (this.music) this.music.setEra(era.index);
  }

  setMusic(track: MusicState): void {
    this.pendingTrack = track;
    if (this.music) this.music.setState(track);
  }

  play(id: string, _x?: number, _y?: number): void {
    if (!this.ctx || !this.sfxGain || this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;
    const minGap = SPECIAL_MIN_GAP[id] ?? BLIPS[id]?.minGap ?? 0.05;
    const last = this.last.get(id) ?? -1;
    if (now - last < minGap) return;

    switch (id) {
      case 'shoot':
        this.last.set(id, now);
        this.sfxShoot(now);
        return;
      case 'kill':
        this.last.set(id, now);
        this.sfxKillPop(now);
        return;
      case 'bossSpawn':
        this.last.set(id, now);
        this.sfxBossSpawn(now);
        return;
      case 'levelUp':
        this.last.set(id, now);
        this.sfxLevelUp(now);
        return;
      case 'timeJump':
        this.last.set(id, now);
        this.sfxTimeJump(now);
        return;
    }

    const b = BLIPS[id];
    if (!b) return;
    this.last.set(id, now);
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = b.type;
    osc.frequency.setValueAtTime(b.freq * this.eraPitch, now);
    if (b.slide) osc.frequency.linearRampToValueAtTime(Math.max(20, b.freq * this.eraPitch + b.slide), now + b.dur);
    g.gain.setValueAtTime(b.gain, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + b.dur);
    osc.connect(g).connect(this.sfxGain);
    osc.start(now);
    osc.stop(now + b.dur + 0.02);
  }

  /* ---------------------------------------------------------------- SFX helpers */

  /** Lazily-built short noise buffer shared by every noise-based SFX transient. */
  private getNoiseBuffer(): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const ctx = this.ctx!;
    const len = Math.max(1, Math.floor(ctx.sampleRate * 0.5));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;
    return buf;
  }

  private noiseBurst(t: number, dur: number, filterType: BiquadFilterType, filterFreq: number, peak: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.getNoiseBuffer();
    const filt = ctx.createBiquadFilter();
    filt.type = filterType;
    filt.frequency.value = filterFreq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filt).connect(g).connect(this.sfxGain!);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** 'shoot': the original triangle blip plus a tiny noise transient for "bite". */
  private sfxShoot(now: number): void {
    const ctx = this.ctx!;
    const freq = 520 * this.eraPitch;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.linearRampToValueAtTime(Math.max(20, freq - 200), now + 0.05);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.08, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    osc.connect(g).connect(this.sfxGain!);
    osc.start(now);
    osc.stop(now + 0.07);
    this.noiseBurst(now, 0.02, 'highpass', 5000, 0.04);
  }

  /** 'kill': two-oscillator pop — a quick low thud plus a brighter click on top. */
  private sfxKillPop(now: number): void {
    const ctx = this.ctx!;
    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(160 * this.eraPitch, now);
    thud.frequency.exponentialRampToValueAtTime(50, now + 0.12);
    const thudGain = ctx.createGain();
    thudGain.gain.setValueAtTime(0.11, now);
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    thud.connect(thudGain).connect(this.sfxGain!);
    thud.start(now);
    thud.stop(now + 0.14);

    const click = ctx.createOscillator();
    click.type = 'triangle';
    click.frequency.setValueAtTime(480 * this.eraPitch, now);
    click.frequency.exponentialRampToValueAtTime(200, now + 0.05);
    const clickGain = ctx.createGain();
    clickGain.gain.setValueAtTime(0.07, now);
    clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    click.connect(clickGain).connect(this.sfxGain!);
    click.start(now);
    click.stop(now + 0.06);
  }

  /** 'bossSpawn': a rising sub-bass plus a swelling noise wash — an "arrival". */
  private sfxBossSpawn(now: number): void {
    const ctx = this.ctx!;
    const dur = 1.5;
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(40, now);
    sub.frequency.linearRampToValueAtTime(90 * this.eraPitch, now + dur);
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0.0001, now);
    subGain.gain.exponentialRampToValueAtTime(0.24, now + dur * 0.7);
    subGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
    sub.connect(subGain).connect(this.sfxGain!);
    sub.start(now);
    sub.stop(now + dur + 0.05);

    const src = ctx.createBufferSource();
    src.buffer = this.getNoiseBuffer();
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.setValueAtTime(200, now);
    filt.frequency.linearRampToValueAtTime(1400, now + dur);
    filt.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.12, now + dur * 0.8);
    g.gain.exponentialRampToValueAtTime(0.001, now + dur);
    src.connect(filt).connect(g).connect(this.sfxGain!);
    src.start(now);
    src.stop(now + dur + 0.05);
  }

  /** 'levelUp': a bright 3-note arpeggio. */
  private sfxLevelUp(now: number): void {
    const ctx = this.ctx!;
    const base = 660 * this.eraPitch;
    const notes = [base, base * 1.25, base * 1.5];
    notes.forEach((freq, i) => {
      const t = now + i * 0.09;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.15, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.connect(g).connect(this.sfxGain!);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  }

  /** 'timeJump': a long rising sweep with a noise sweep underneath. */
  private sfxTimeJump(now: number): void {
    const ctx = this.ctx!;
    const dur = 2.5;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(1600 * this.eraPitch, now + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.22, now + dur * 0.4);
    g.gain.exponentialRampToValueAtTime(0.001, now + dur);
    osc.connect(g).connect(this.sfxGain!);
    osc.start(now);
    osc.stop(now + dur + 0.05);

    const src = ctx.createBufferSource();
    src.buffer = this.getNoiseBuffer();
    const filt = ctx.createBiquadFilter();
    filt.type = 'bandpass';
    filt.frequency.setValueAtTime(300, now);
    filt.frequency.exponentialRampToValueAtTime(3000, now + dur);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, now);
    ng.gain.linearRampToValueAtTime(0.1, now + dur * 0.5);
    ng.gain.exponentialRampToValueAtTime(0.001, now + dur);
    src.connect(filt).connect(ng).connect(this.sfxGain!);
    src.start(now);
    src.stop(now + dur + 0.05);
  }
}

export class NullAudio implements AudioHooks {
  play(): void {}
  setEra(): void {}
  setMusic(): void {}
  setVolume(): void {}
  setMusicVolume(): void {}
  setSfxVolume(): void {}
  resume(): void {}
}
